import {useState} from 'react';
import {useNavigate, useParams, useSearchParams} from 'react-router-dom';
import {useIntl} from 'react-intl';
import {PageTitle} from '../../../../_metronic/layout/core';
import TabComponent from '../../../../app/common/components/TabComponent';
import {useGetServiceInfo} from '../../Hooks/ServiceManager';
import {useGetContactInfo} from '../../Hooks/ContactManager';
import {
  isCentrexServiceName,
  isEssentielleServiceName,
  isPabxServiceName,
  shouldOpenInternalTelephonyDetails,
} from '../../Helpers/ServiceHelper';
import Loading from '../../../../app/common/components/Loading';
import SuiviConsommation from './TrunkSip/SuiviConsommation';
import GererNumeros from './TrunkSip/GererNumeros';
import RestrictionAppels from './TrunkSip/RestrictionAppels';
import LocalisationsUrgence from './TrunkSip/LocalisationsUrgence';
import GererMessages from './TrunkSip/GererMessages';
import GererMessagesVocaux from './TrunkSip/GererMessagesVocaux';
import ConfigurerTrunkSip from './TrunkSip/ConfigurerTrunkSip';
import GererTelephonesIp from './GererTelephonesIp';
import VirtualPbx from './Centrex/VirtualPbx';
import CtiGateway from './Centrex/CtiGateway';
import CentrexAnnuaire from './Centrex/Annuaire';
import CentrexSoftphonesCeleste from './Centrex/SoftphonesCeleste';
import ConfigurationSVI from './ConfigurationSVI';
import ConsommationsDetaillees from './ConsommationsDetaillees';
import TelephonyServiceHeader from './TelephonyServiceHeader';
import '../../Styles/Telephonie/Style.css';
import NotFoundRedirect from '../../../../app/common/components/NotFoundRedirect';
import {isNotFoundError} from '../../../../app/common/tools/httpError';

const SHOW_CONFIG_TRUNK_SIP_TAB = true;

/**
 * Page de détail d'une prestation de téléphonie. Les onglets Trunk SIP / Centrex ne sont
 * proposés qu'aux services éligibles (cf. shouldOpenInternalTelephonyDetails) ; ces
 * services n'ont alors plus les onglets Config SVI / Conso détaillées (ancien menu).
 *
 * La configuration (MyTelephony / MyIstra / Gestion 4G) n'est volontairement pas
 * reprise ici : elle est déjà portée par TelephonyConfigPage
 * (route `services/telephonie/configuration`).
 */
const DetailsTelephonie = (): JSX.Element => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {serviceId: cisId = ''} = useParams<{serviceId: string}>();
  const {
    data: dataService,
    isError: isErrorService,
    error: errorService,
    isLoading: loadingService,
  } = useGetServiceInfo(cisId);
  const {data: dataContact} = useGetContactInfo();
  // Nom du service issu de l'endpoint (GET api/services/{serviceId}), pas de l'URL
  const serviceName = dataService?.serviceName ?? '';
  const isCentrex = isCentrexServiceName(serviceName);
  const isPabx = isPabxServiceName(serviceName);
  const isEssentielle = isEssentielleServiceName(serviceName);
  // Directement dérivé du cisId de l'URL plutôt que de dataService.serviceId :
  // même valeur (le serviceId CIS/`link_id` est transmis tel quel, résolu côté
  // back vers l'id VTX par TelephonieServiceIdResolver), mais disponible tout
  // de suite — les onglets Trunk SIP n'ont donc pas besoin d'attendre la fin
  // du (lent) chargement des infos générales du service pour lancer leurs
  // propres requêtes.
  const isTrunkSip =
    searchParams.get('trunkSip') === '1' ||
    (dataService != null && shouldOpenInternalTelephonyDetails(dataService));
  const trunkSipServiceId = cisId && isTrunkSip ? Number(cisId) : undefined;

  const [selectedKey, setSelectedKey] = useState<string | undefined>(
    searchParams.get('tab') ?? undefined
  );
  const activeKey = selectedKey ?? (isTrunkSip ? 'tab-conso' : 'consodetaillees');
  const [numeroConsommation, setNumeroConsommation] = useState<{
    numero: string;
    cle: number;
  } | null>(null);
  const [numeroRestriction, setNumeroRestriction] = useState<{
    numero: string;
    numeroId: number;
  } | null>(null);

  if (isNotFoundError(errorService)) {
    return <NotFoundRedirect />;
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>{dataService?.link_name}</PageTitle>

      {isErrorService && <p>{intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'})}</p>}

      {loadingService && <Loading className="col-md-6" />}

      {dataService != null && (
        <TelephonyServiceHeader
          service={dataService}
          onBack={() => navigate('/services/telephonie/liste')}
        />
      )}

      <TabComponent
        tabData={[
          ...(isTrunkSip
            ? [
                {
                  title: intl.formatMessage({id: 'MENU.TELEPHONY.CONSUMPTION'}),
                  key: 'tab-conso',
                  content:
                    trunkSipServiceId !== undefined ? (
                      <SuiviConsommation
                        serviceId={trunkSipServiceId}
                        isCentrex={isCentrex}
                        numeroAPreselectionner={numeroConsommation}
                      />
                    ) : (
                      <></>
                    ),
                },
                {
                  title: intl.formatMessage({id: 'DETAILS_TELEPHONIE.TAB.NUMEROS'}),
                  key: 'tab-numeros',
                  content:
                    trunkSipServiceId !== undefined ? (
                      <GererNumeros
                        serviceId={trunkSipServiceId}
                        onConsulterConsommation={(numero) => {
                          setNumeroConsommation({numero, cle: Date.now()});
                          setSelectedKey('tab-conso');
                        }}
                        onModifierRestriction={(numero) => {
                          setNumeroRestriction(numero);
                          setSelectedKey('tab-restriction');
                        }}
                        isCentrex={isCentrex}
                        isPabx={isPabx}
                        isEssentielle={isEssentielle}
                        isActive={activeKey === 'tab-numeros'}
                      />
                    ) : (
                      <></>
                    ),
                },
                {
                  title: intl.formatMessage({id: 'DETAILS_TELEPHONIE.TAB.RESTRICTION'}),
                  key: 'tab-restriction',
                  content:
                    trunkSipServiceId !== undefined ? (
                      <RestrictionAppels
                        serviceId={trunkSipServiceId}
                        isActive={activeKey === 'tab-restriction'}
                        numeroInitial={numeroRestriction}
                      />
                    ) : (
                      <></>
                    ),
                },
                {
                  title: intl.formatMessage({id: 'DETAILS_TELEPHONIE.TAB.LOCALISATIONS'}),
                  key: 'tab-localisations',
                  content:
                    trunkSipServiceId !== undefined ? (
                      <LocalisationsUrgence
                        serviceId={trunkSipServiceId}
                        isActive={activeKey === 'tab-localisations'}
                      />
                    ) : (
                      <></>
                    ),
                },
                ...(!isCentrex && !isEssentielle && SHOW_CONFIG_TRUNK_SIP_TAB
                  ? [
                      {
                        title: intl.formatMessage({id: 'DETAILS_TELEPHONIE.TAB.CONFIG_TRUNK_SIP'}),
                        key: 'tab-config-trunk-sip',
                        content:
                          trunkSipServiceId !== undefined ? (
                            <ConfigurerTrunkSip serviceId={trunkSipServiceId} />
                          ) : (
                            <></>
                          ),
                      },
                    ]
                  : []),
                ...(isEssentielle || isCentrex
                  ? [
                      {
                        title: intl.formatMessage({id: 'DETAILS_TELEPHONIE.TAB.MESSAGES'}),
                        key: 'tab-messages',
                        content: isCentrex ? (
                          <GererMessages />
                        ) : trunkSipServiceId !== undefined ? (
                          <GererMessagesVocaux
                            serviceId={trunkSipServiceId}
                            isActive={activeKey === 'tab-messages'}
                          />
                        ) : (
                          <></>
                        ),
                      },
                    ]
                  : []),
                ...(isCentrex
                  ? [
                      {
                        title: intl.formatMessage({id: 'DETAILS_TELEPHONIE.TAB.TELEPHONES_IP'}),
                        key: 'tab-telephones-ip',
                        content: <GererTelephonesIp />,
                      },
                      {
                        title: intl.formatMessage({id: 'DETAILS_TELEPHONIE.TAB.PBX'}),
                        key: 'tab-pbx',
                        content: <VirtualPbx />,
                      },
                      {
                        title: intl.formatMessage({id: 'DETAILS_TELEPHONIE.TAB.CTI'}),
                        key: 'tab-cti',
                        content: <CtiGateway />,
                      },
                      {
                        title: intl.formatMessage({id: 'DOCUMENTS.TELEPHONIE.ANNUAIRE'}),
                        key: 'tab-annuaire',
                        content: <CentrexAnnuaire />,
                      },
                      {
                        title: intl.formatMessage({id: 'DETAILS_TELEPHONIE.TAB.SOFTPHONES'}),
                        key: 'tab-softphones',
                        content: <CentrexSoftphonesCeleste />,
                      },
                    ]
                  : []),
              ]
            : []),
          // Clients migrés (nouveau menu) : pas d'onglets Config SVI / Conso détaillées
          ...(!isTrunkSip && dataService != null && dataContact != null
            ? [
                {
                  title: intl.formatMessage({id: 'DETAILS_TELEPHONIE.TAB.CONFIG_SVI'}),
                  key: 'configurationsvi',
                  content: (
                    <ConfigurationSVI
                      CISID={cisId}
                      tel={dataService.tel1 ?? undefined}
                      telperso={dataContact.fixe}
                    />
                  ),
                },
              ]
            : []),
          ...(!isTrunkSip
            ? [
                {
                  title: intl.formatMessage({id: 'TELEPHONIE.CONSO.TITLE'}),
                  key: 'consodetaillees',
                  content: <ConsommationsDetaillees serviceId={cisId} />,
                },
              ]
            : []),
        ]}
        activeKey={activeKey}
        onTabChange={setSelectedKey}
        scrollable
      />
    </>
  );
};

export default DetailsTelephonie;
